import type { NightmareType } from './nightmares.ts';
import type { SagaCond } from './saga.ts';
import type { SendaDef, SendaLine } from './sendas.ts';
import { sec } from './units.ts';

/**
 * The druid's path, «Las cuatro estaciones» (docs/sendas/druida.md): the numbers of its fights and the path itself.
 * Chapter 1, spring: the Circle's wood flooded by the thaw under the Grandsire. Life of the act's bosses in seconds of
 * D, blows in % of the life of who takes them (bp), the people's life in blows (docs/SENDAS.md, «Calibración»).
 */

export const DRUID_PATH = {
  /**
   * The thaw (act 1; battle/druid.ts): up the wood's slope to Olmo's clearing before the water. The melt comes down in
   * surges: one every `surge.every` from `surge.first`, its roar and foam at the top `surge.warn` before it starts down
   * (3 s at least), a wall of water `surge.len` long going down at `surge.speed` a tick; whoever it catches off a mound
   * wades and drowns, carried `surge.drift` a tick down the slope. From below the flood rises (`rise`: from `rise.first`,
   * `rise.speed` a tick). In water a hero goes at `wadeBp` and loses `drownBp` of its life a second (10 %, the
   * document's); in the mud at `mudBp`. Each surge sends `wolves` wolves to the mound ahead of the hero (they flee the
   * water too); a patch of mud wakes `slimes` slimes as the hero comes within `wake`. Won within `reach` of the
   * clearing's middle. `hpBp`, `dmgBp`: the act's nightmares over the chapter's commons.
   */
  thaw: {
    surge: { first: sec(8), every: sec(15), warn: sec(3), len: 6000, speed: 160, drift: 14 },
    rise: { first: sec(24), speed: 12 },
    wadeBp: 5500,
    drownBp: 1000,
    mudBp: 6000,
    wolves: 2,
    wolfFrom: sec(20),
    slimes: 1,
    wake: 3600,
    reach: 2600,
    hpBp: 10000,
    dmgBp: 10000,
  },
  /**
   * Olmo's clearing (act 2; battle/druid.ts): Ceda looks for the face in the old trunk for `search` (the bar, 3 min).
   * Hob fences the shoots one after another, `fence` each (5 s; a blow stops him a moment). Slimes come up out of the
   * mud pools at the rim: `slime.n` every `slime.every` from `slime.first`, one more from each `slime.more`; each goes for
   * the nearest shoot not fenced, eats it in `eat` standing on it, and grows: its life × (1 + `growBp`), its blow ×
   * (1 + `blowBp`). Wolves now and then (`wolves`: at those ticks, `n` each). Ceda and Hob take `hits` blows each.
   * The clearing keeps sprouting while Ceda looks (the thaw's spring): `up` shoots at the start of the `shoots` spots,
   * one more every `sprout`, in Hob's order; Hob waits where the next comes up. A hungry slime walks past the hero
   * to its shoot; one that ate goes for Hob, the one who fences them, each of its blows `fedBlows` heavier for every
   * shoot it ate (the shoots kept are what keeps Hob standing).
   */
  glade: {
    search: sec(180),
    shoots: 12,
    up: 4,
    sprout: sec(8),
    fence: sec(5),
    hits: 15,
    eat: sec(2),
    growBp: 6000,
    blowBp: 3000,
    fedBlows: 2,
    slime: { first: sec(5), every: sec(10.5), n: 1, more: sec(90) },
    wolves: { at: [sec(45), sec(100), sec(150)] as number[], n: 2 },
    hpBp: 3200,
    dmgBp: 10000,
  },
  /**
   * The root bridge (act 3, the test; battle/druid.ts; §4, puzzle 1): the roots' rule (`per`: ticks a hex, `see` hexes
   * they see, `near`: two arrivals at a sprout closer than this tangle). A tap on a stone sends the hero to throw a seed
   * on it (standing still `throw` within `reach`; `seeds`: the hero's three and Ceda's five); a tap on a knot, to cut it
   * (its Raíz: still `cut` within `reach`). The Lodazal (`lodazal`: `d` seconds of D) lives in the stream: every `every`
   * from `first` it goes for a sprout a root still wants, bubbles there `warn` (3 s) and eats it over `eat` unless struck
   * `scareBp` of its life meanwhile (it sinks); then it comes up at the bank by the hero, raises its arm `warn` and
   * slaps round `slapR` (`slapBp`), stays up `up` and sinks. From `wave.first`, every `wave.every`, it throws the mud of
   * the flood over the bank (`wave.warn` before): `wave.heroBp` and slowed `wave.slow` at `wave.slowBp` to whoever isn't
   * on a mound. Slimes come up at the water's edge (`slimes`), and with every knot; wolves out of the wood (`wolves`).
   * With both roots on the islet the bridge braids for `weave` (16 s): the Lodazal comes up out of the stream for good
   * and fights on the bank, a slap after another; won when the bridge is braided, or the Lodazal down.
   */
  roots: {
    per: sec(3),
    see: 3,
    near: sec(3),
    seeds: 8,
    throw: sec(1),
    cut: sec(5),
    reach: 7200,
    weave: sec(16),
    lodazal: { d: 70, first: sec(6), every: sec(10), warn: sec(3), eat: sec(3), scareBp: 900, slapBp: 2500, slapR: 1900, up: sec(5), pace: 90 },
    wave: { first: sec(18), every: sec(20), warn: sec(3), heroBp: 2200, slowBp: 4500, slow: sec(3) },
    slimes: { first: sec(4), every: sec(10), n: 1, knot: 2 },
    wolves: { first: sec(16), every: sec(30), n: 2 },
    hpBp: 3500,
    dmgBp: 10000,
  },
  /** The nightmares of the thaw and the clearing (their kinds). */
  kinds: { wolf: 'wolf' as NightmareType, slime: 'slime' as NightmareType, wyvern: 'wyvern' as NightmareType, crow: 'crow' as NightmareType, serpent: 'shellSerpent' as NightmareType, frost: 'rime' as NightmareType },
  /**
   * Chapter 2, summer: the kilns (act 1; battle/druid-summer.ts). Hob covers the six kilns with earth, `cover` each
   * (a blow stops him); the nearest alight first. Ash wyverns about the slope dive at a kiln (`wyv`: `n` from `first`,
   * one more each `more` up to `max`; each every `every`, its dive `dive`, 3 s, a shadow on the kiln; its life `d`
   * seconds of D): up in the smoke nothing reaches it; diving, unless struck `scareBp` of its life it sets the kiln
   * alight. A kiln alight smoulders `fuse`, then cracks with white smoke
   * `warn` (3 s), then bursts: `burstBp` to whoever stands within `burstR`, `burstHob` blows to Hob. Won with every kiln
   * covered (or burst, but `lose` burst lose it); lost with Hob down. Soot crows (`crows`) out of the smoke.
   */
  kilns: {
    n: 6,
    cover: sec(5),
    hits: 15,
    fuse: sec(9),
    warn: sec(3),
    burstBp: 3000,
    burstR: 2300,
    burstHob: 4,
    lose: 3,
    wyv: { d: 14, first: sec(5), every: sec(10), dive: sec(3), scareBp: 1400, n: 2, more: sec(30), max: 4 },
    crows: { first: sec(8), every: sec(14), n: 6 },
    hpBp: 25000,
    dmgBp: 17000,
  },
  /**
   * Olmo's pine (act 2): Hob fells the strip's `trees`, `fell` each (a blow stops him), before the great fire comes up
   * from the valley to it (`arrive` from the start: lost if it gets there first; who stands below its line burns
   * `frontBp` a second). The wind throws embers ahead of it (`ember`: `n` every `every` from `first`, at the hero and at
   * Hob's next tree; white smoke `warn` (3 s) where it falls): `hitBp` to whoever is under it, `hob` blows to Hob, and
   * it burns `burn` round `r` (`heatBp` a second; Hob can't work in it). Soot crows; wolves running from the fire.
   */
  strip: {
    trees: 7,
    fell: sec(5),
    hits: 15,
    arrive: sec(120),
    frontBp: 1200,
    ember: { first: sec(7), every: sec(8), n: 2, warn: sec(3), r: 1300, burn: sec(6), hitBp: 2200, heatBp: 800, hob: 2 },
    crows: { first: sec(10), every: sec(14), n: 6 },
    wolves: { first: sec(28), every: sec(28), n: 2 },
    hpBp: 12000,
    dmgBp: 38000,
  },
  /**
   * The counterfire (act 3, the test; §4, puzzle 2; the rule in battle/druid-summer-layout.ts): a patch burns `burn`,
   * catches the next the wind's way after `catch`; the great fire climbs a band each `climb`. The wind turns once,
   * between `flipMin` and `flipMax` (the smoke bends `warn` before, 5 s); the great fire reaches the patches under the
   * pine `room` after the turn. A tap on a patch sends the hero to light it with Hob's ember (`throw` standing still
   * within `reach`). `marks` scorched and the pine is lost; who stands in a patch alight burns `heatBp` a second. The old
   * wyvern (`wyv`: `d` seconds of D) dives at a patch whose fire would reach the pine (3 s: smoke where it goes) and
   * lights it unless struck `scareBp` of its life meanwhile; its breath (`breath`) down a line through the hero, warned
   * 3 s on the ground: `bp`, `w` wide, `len` long. Soot crows (`crows`).
   */
  counter: {
    burn: sec(10),
    catch: sec(4),
    climb: sec(15),
    flipMin: sec(50),
    flipMax: sec(62),
    room: sec(20),
    warn: sec(5),
    throw: sec(1),
    reach: 2400,
    marks: 3,
    heatBp: 900,
    wyv: { d: 90, first: sec(9), every: sec(11), dive: sec(3), scareBp: 250, pace: 140 },
    breath: { first: sec(12), every: sec(10), warn: sec(3), bp: 2500, w: 1200, len: 11000 },
    crows: { first: sec(14), every: sec(16), n: 5 },
    hpBp: 20000,
    dmgBp: 32000,
  },
  /**
   * Chapter 3, autumn: under the leaves (act 1; battle/druid-autumn.ts). Ceda walks the wood's way to Olmo's maple at
   * `pace` a tick, while the hero is within `lead` of her and nothing surfaced is within `fear`; won with her within
   * `reach` of the maple's foot. Stalkers under the leaves (`stalk`: from `first`, one each `every`, `max` at once):
   * they go for Ceda unseen (only their furrow shows: nothing reaches them), and within `surfR` of her they come up
   * (`warn`, 3 s, the leaves heaving), out `up`, then under again. The druid's Raíz (still `still`) catches every one
   * under the leaves within `rootR`: pulled up, held `hold`, in reach. Weavers (`weave`: from `first`, one each `every`,
   * `max` at once; `hpBp` of a common's life) keep at `range` off Ceda and spin silk at her every `spin` (`warn` before, a line on the ground):
   * a cocoon (`cocoonHp` of a common's life) stops her, and while it holds she takes a blow each `squeeze`. Hob follows.
   */
  leaves: {
    pace: 20,
    lead: 3400,
    fear: 1400,
    reach: 1600,
    still: sec(2),
    stalk: { first: sec(4), every: sec(6), max: 5, surfR: 1000, warn: sec(3), up: sec(5), rootR: 1800, hold: sec(3), pace: 70 },
    weave: { first: sec(12), every: sec(24), max: 2, range: 3800, spin: sec(9), warn: sec(3), hpBp: 6000, cocoonHp: 6000, squeeze: sec(3) },
    hits: 15,
    hpBp: 7500,
    dmgBp: 26000,
  },
  /**
   * The memory, Olmo's autumn (act 2; battle/druid-autumn.ts), a hundred years back: the rot comes down the slope from
   * the maple's roots to Los Hornos along roots that fork. Each wave starts at a root's top (`waves`: when, which root)
   * and its head goes a hex each `per`, splitting where the root forks; nothing strikes a head (it isn't a nightmare:
   * it is the root going black). A tap on a root sends Olmo to stand on it; still `cut` (his Raíz) and the root is cut
   * there: a head that comes to it dies; one already past goes on. A head that reaches the houses loses the act. A
   * head coming into a hex lashes whoever stands within `lash.r` (`lash.bp`); at every other fork a rootwight (`wightBp`
   * of a common's life) climbs out of it.
   * Paz follows Olmo (vital, `paz.hits` blows) and the colliers keep by the houses (`colliers`); each strikes the
   * nearest nightmare within `chopR` every second for `chopBp` of its life. Leeches out of the rot (`leech`).
   */
  blight: {
    per: sec(2.6),
    cut: sec(2),
    reach: 750,
    waves: [
      { t: sec(4), src: 1 },
      { t: sec(20), src: 0 },
      { t: sec(34), src: 2 },
      { t: sec(46), src: 1 },
      { t: sec(58), src: 0 },
      { t: sec(58), src: 2 },
      { t: sec(74), src: 1 },
      { t: sec(86), src: 2 },
      { t: sec(100), src: 0 },
    ] as { t: number; src: number }[],
    lash: { r: 650, bp: 1200 },
    wightBp: 3000,
    paz: { hits: 15, chopBp: 2000 },
    colliers: { n: 3, hits: 10, chopBp: 1500 },
    chopR: 1200,
    leech: { first: sec(14), every: sec(13), n: 2 },
    hpBp: 12000,
    dmgBp: 8000,
  },
  /**
   * What lies below (act 3, the test; §4, puzzle 3; the net in battle/druid-autumn-layout.ts): the rot's roots from
   * the knot to the maple, some in sight, some under the ground; the roots that hold the maple (in sight with Olmo's
   * notch, under with white mushrooms). The rot has the first `pre` cells of each of its roots as the fight starts (the
   * ground shows them). A tap on a cell sends the hero to stand on it; still `cut` (its Raíz) and everything under that
   * cell is cut; a cut that touches a rotten root bites back up the druid's own root (`bite` of its life: «Mirar es
   * gratis. Cortar, no.»). The rot wakes with the first cut (or at `wake`) and goes a hex each `step`,
   * a puffball out of it each time (`puffBp` of a common's life); a root cut ahead of it stops it; the rot in the maple takes a branch, and another
   * each `eat`; a cut in vain (the rot already past) or on a root that holds the maple takes a branch; `branches` and
   * the maple falls. The broodmother (`boss`: `d` seconds of D) leaps at the hero (its spot marked `warn`, 3 s: `bp`
   * round `r`), webs the maple (`web`: a cocoon of `hpBp` of a common's life that takes a branch unless struck down
   * within `cut`) and lets out its brood (`brood`): while one is on the hero (within `r`) its silk slows it `slowBp`, and
   * the leap is harder to get out from under.
   */
  under: {
    pre: 3,
    bite: 1500,
    step: sec(8),
    wake: sec(50),
    cut: sec(2),
    reach: 750,
    branches: 3,
    eat: sec(10),
    boss: { d: 110, first: sec(8), every: sec(9), warn: sec(3), bp: 2200, r: 1800, pace: 120 },
    web: { first: sec(16), every: sec(20), cut: sec(10), hpBp: 3500 },
    brood: { first: sec(14), every: sec(13), n: 4, r: 900, slowBp: 4500 },
    puffBp: 4500,
    hpBp: 15000,
    dmgBp: 10000,
  },
  /**
   * Chapter 4, winter: the boughs (act 1; battle/druid-winter.ts). Up the Grandsire's crown over the clouds, bough to
   * bough, to its top (won within `reach` of it). Snow comes off the boughs above in blocks: every `snow.every` from
   * `snow.first`, `snow.n` of them, one where the hero stands and the next where it is going (`snow.lead` ahead on the
   * way), the bough creaking and snow dust falling `snow.warn` (3 s) before: `snow.bp` to whoever is within `snow.r`;
   * each leaves a heap that slows `heapBp` for `heap` within `heapR`. Soot crows that nest in the crown come down in
   * flocks (`crows`). Between one bough and the next, cloud: the wind swings them together for `sway.open` every
   * `sway.every` (each gap `sway.lag` after the one below), `sway.warn` of them coming close before. Ceda and Hob come up
   * behind, out of the fight.
   */
  boughs: {
    reach: 1700,
    sway: { every: sec(17), open: sec(5), warn: sec(2), lag: sec(7) },
    snow: { first: sec(5), every: sec(4), n: 2, warn: sec(3), r: 1150, bp: 3500, lead: 2400, heap: sec(6), heapR: 1000, heapBp: 4500 },
    crows: { first: sec(6), every: sec(7), n: 5 },
    hpBp: 24000,
    dmgBp: 9500,
  },
  /**
   * The wood that opens (act 2): Hob down in a hollow of the crown with his leg, Ceda bandaging it for `need` (90 s; a
   * bite on her stops her `flinch`). Shell serpents come out of the wood (`serp`; their life `d` seconds of D): under it
   * nothing reaches them; they come out next to whoever stands still (the hero once still `still`; Hob and Ceda always),
   * the bark swelling `warn` (3 s) before, and bite round it (`bp` of a hero's life, `npc` blows to a person). Standing
   * on the swelling treads it down: the hero takes `stompBp` and nobody else is bitten, the serpent comes up stunned.
   */
  hollow: {
    need: sec(90),
    flinch: sec(1.5),
    still: sec(1),
    serp: { d: 5, first: sec(4), every: sec(4.75), max: 5, travel: sec(3), spread: sec(2), warn: sec(3), r: 950, bp: 2500, npc: 2, npcEvery: 1, stomp: 700, stompBp: 1000, stun: sec(3), up: sec(4) },
    hob: 10,
    ceda: 15,
    hpBp: 10000,
    dmgBp: 10000,
  },
  /**
   * The sap of the crown (act 3, the test; §4, puzzle 4; the crowns in battle/druid-winter-layout.ts): a pulse of 12
   * each `pulse` up the trunk, split even at each fork among the open branches; a bud that gets what it asks opens, more
   * and it cracks (shut for `crack`); `need` open at once and the crown runs (the low crown first, then the high). The
   * hero still `cut` within `reach` of a knot turns it (its Raíz), and the frozen wood bites back `chillBp` of its life.
   * The crown runs once `need` buds stay open `hold` pulses in a row.
   * The frost that climbs (`boss`: `d` seconds of D) comes up the trunk to a closed knot, frosts it `warn` (3 s) and
   * opens it unless struck `scareBp` of its life meanwhile; its claw at the hero (`claw`: warned 3 s on the ground).
   * The serpents come out by the hero when it stands still (`serp`); with the hero going, one in `npcEvery` goes for
   * Hob in his hollow.
   */
  sap: {
    pulse: sec(3),
    crack: sec(8),
    need: 3,
    hold: 2,
    cut: sec(2),
    reach: 750,
    chillBp: 2450,
    still: sec(1),
    boss: { d: 135, first: sec(14), pace: 60, warn: sec(3), scareBp: 100, rest: sec(5), claw: { first: sec(8), every: sec(7), warn: sec(3), r: 1500, bp: 3000 } },
    serp: { d: 5, first: sec(8), every: sec(11), max: 2, travel: sec(4), spread: sec(3), warn: sec(3), r: 950, bp: 2500, npc: 1, npcEvery: 3, stomp: 700, stompBp: 1000, stun: sec(3), up: sec(4) },
    hob: 10,
    hpBp: 12000,
    dmgBp: 10000,
  },
  /**
   * Chapter 5, inside the Grandsire: the buds below (act 2; battle/druid-roots.ts; the lessons of chapters 4 and 1). The
   * sap comes down the gallery from its top in a pulse every `pulse`, split even at each fork among the branches whose
   * knot is open (the crown's rule, battle/druid-winter-layout.ts `sapFlow`). A mouth stopped with rot opens for good on
   * a pulse that gives it what it asks (its notches); more cracks it: it spits rot round it (`spit`: `bp` within `r`, a
   * slime out of it) and stays shut `crack`. The hero turns a knot standing still `cut` within `reach` (its Raíz); the
   * rot in the wood bites back `biteBp`. Under an open mouth its sound roots wake (the bridge's rule, battle/druid-layout.ts:
   * `per`, `see`, `near`); a tap on a stone sends the hero to throw a moss seed on it (`throw` within `sow`); a tangle is
   * cut standing still `knot` by it. The mouth is clean with both its roots in it. The rot drips from the roof on the
   * hero (`drip`: its spot dark `warn` before); rotten roots crawl out of the mouths still shut (`slimes`). Ceda waits by the
   * gallery's top and takes `ceda` blows.
   */
  mouths: {
    pulse: sec(3),
    crack: sec(6),
    spit: { bp: 1800, r: 1700 },
    cut: sec(2),
    reach: 750,
    biteBp: 1300,
    per: sec(3),
    see: 3,
    near: sec(3),
    throw: sec(1),
    sow: 6400,
    knot: sec(4),
    drip: { first: sec(9), every: sec(10), warn: sec(3), r: 1250, bp: 2600 },
    slimes: { first: sec(8), every: sec(20), n: 1 },
    ceda: 25,
    hpBp: 8000,
    dmgBp: 2000,
  },
  /**
   * The net round the chamber (act 3, the lesson of chapter 3; battle/druid-autumn.ts `under`, with the chamber of the
   * Carcoma where the maple stood): the same numbers' shape as `under`.
   */
  net: {
    pre: 3,
    bite: 2200,
    step: sec(5),
    wake: sec(45),
    cut: sec(2),
    reach: 750,
    branches: 3,
    eat: sec(10),
    boss: { d: 110, first: sec(8), every: sec(9), warn: sec(3), bp: 3000, r: 1800, pace: 120 },
    web: { first: sec(16), every: sec(20), cut: sec(10), hpBp: 3500 },
    brood: { first: sec(14), every: sec(13), n: 4, r: 900, slowBp: 4500 },
    puffBp: 4500,
    hpBp: 15000,
    dmgBp: 10000,
  },
  /**
   * The Carcoma (act 4, the path's enemy; battle/druid-roots.ts; §5). Its life `d` seconds of D with every root cut;
   * while its roots feed it, it takes `takenBp[cut]` of every blow (four roots, none; three, a quarter…). A root cut
   * stays cut while another feeds it; with none, one grows again `regrow` later (`healed`: the Grandsire's branch on it;
   * `released`: the seed to keep), its thread on the wall `thread` before, and the Carcoma is pulled back to the middle;
   * at or under `lastBp` of its life none grows. Each root is cut by its lesson:
   *
   * - mud (chapter 1): two of its stones sown in the order that doesn't tangle (a root net of content ROOT_NETS; the
   *   bridge's `per`, `see`, `near` from `mouths`); a tangle cut standing still `knot`.
   * - resin (chapter 2): its patches across the root, the air of the chamber running along them one way (the smoke
   *   shows it); a patch lit with Hob's ember (`resin.throw` within `resin.reach`) burns `burn` and lights the next the
   *   air's way after `catch`; the fire in the root's patch burns it. Standing in a patch alight: `heatBp` a second.
   * - hidden (chapter 3): where it goes under the wood, mushrooms on the floor (black over it, white over a sound root);
   *   the hero still `cut` on a cell (within `reach`) cuts what is under it; a sound root cut bites back `wrongBp`.
   * - frost (chapter 4): three buds round it, fed from a spring in the wall by a crown of forks and knots (`pulse`); all
   *   three open on one pulse and it melts. A knot turned standing still `cut` bites `chillBp`.
   *
   * Its phases by roots left: with four or three it spits splinters at the hero in a cone (`cone`: `len` long, `half`
   * of a turn in bp each side, `bp`, marked `warn`); with two or one it crawls at the hero (`crawl`, never farther than
   * `tether` from the middle: the root still holds her) leaving a trail of
   * rot that slows (`trail`) and bites round it (`bite`); with none it goes for the colliers at the door (`run` a tick;
   * lost within `door` of it), held `gate` by each of Elvira's knots of sound root the hero stands still by `touch`
   * (once each while bare). Slimes out of the walls (`slimes`) for the colliers, who chop what reaches them (`chop`:
   * `bp` of its life every `every`); their life `colliers` blows each. The branch (`healed`): `bp` of its life every
   * `every`. The seed (`released`): a root's swipe at the hero who carries it every `every` (marked `warn`, `r`):
   * `heroBp`, and a crack in the seed; `seed` cracks and it breaks (lost). Soft time: from `rot.at` the floor rots in from
   * the walls (`rot.half`: half the floor left), `rot.bp` a second outside. A scar (a try lost past her half) is a root
   * already cut as the next try starts (SENDA.scars.max at most).
   */
  carcoma: {
    d: 160,
    takenBp: [0, 2500, 5000, 7500, 10000] as number[],
    regrow: { healed: sec(40), released: sec(75), thread: sec(10), lastBp: 2500 },
    resin: { burn: sec(5), catch: sec(2), heatBp: 900, throw: sec(1), reach: 2600 },
    cut: sec(2),
    reach: 750,
    wrongBp: 2500,
    chillBp: 1200,
    pulse: sec(3),
    knot: sec(4),
    cone: { first: sec(6), every: sec(7), warn: sec(3), len: 6500, half: 1100, bp: 3000 },
    crawl: { pace: 40, tether: 2200 },
    trail: { every: sec(1), last: sec(12), r: 700, slowBp: 4000 },
    bite: { every: sec(6), warn: sec(3), r: 1100, bp: 4500 },
    run: 6,
    door: 1400,
    gate: sec(7),
    touch: sec(1),
    slimes: { first: sec(10), every: sec(18), n: 2 },
    /** The rotten roots out of the walls, by the decision (the branches' knob): every `healed`, every `released`. */
    wallsEvery: { healed: sec(16.5), released: sec(26) },
    chop: { every: sec(1.5), bp: 4000 },
    colliers: 12,
    healed: { every: sec(20), bp: 100 },
    released: { first: sec(20), every: sec(30), warn: sec(3), r: 1300, heroBp: 600 },
    seed: 3,
    rot: { at: sec(600), half: sec(780), bp: 700 },
    hpBp: 8000,
    dmgBp: 6500,
  },
};

/**
 * The root nets of the test (docs/sendas/druida.md §4, «El puente de raíces»): each `[q, r]` of the left root, the
 * right one (on the bank, row 4), the islet, then the stones. Drawn once by a search over random nets (the check is
 * `rootsSolve` in packages/sim battle/druid-layout.ts, held by a test): sowing everything at once tangles them, one or
 * two sets of stones win, and the winning plans can be played a beat late. The first is the document's example. A fight
 * takes one, or its mirror, by its seed.
 */
export const ROOT_NETS: readonly (readonly number[])[] = [
  [-4, 4, 0, 4, 0, -2, -2, 2, 0, 2, 0, 0],
  [-5, 4, -1, 4, 0, -2, -2, -1, -3, 2, -1, 2, -1, 0],
  [-5, 4, -1, 4, 0, -2, -2, -1, -3, 2, -1, 2, 2, 2, 2, -1, -1, 0],
];

const ln = (id: string, who: string, cond?: SagaCond): SendaLine => (cond ? { id, who, if: cond } : { id, who });

/**
 * The druid's path, «Las cuatro estaciones». Chapter 1, spring («La primera hoja»): a leaf of the Grandsire falls in the
 * bastion's Grove, black inside; Ceda, new to the Circle of the Moss; up the flooded wood before the thaw (the mounds
 * save you, the mud slows you); Hob up a tree in Olmo's clearing; the clearing held while Ceda looks for the face and Hob
 * fences the shoots; the campfire under a roof of roots in the rain (to Hob); the test, the bridge of roots across the
 * swollen stream, the Lodazal; the close with the first face of Olmo, and Espino among the trees with his curved knife.
 */
export const DRUID_SENDA: SendaDef = {
  id: 'druid',
  cls: 'druid',
  planned: 5,
  cast: {
    woman: { model: 'ceda' },
    ceda: { model: 'ceda' },
    collier: { model: 'hob' },
    hob: { model: 'hob' },
    oldman: { model: 'espino' },
    espino: { model: 'espino' },
    oldwoman: { model: 'ruda' },
    ruda: { model: 'ruda' },
    elvira: { model: 'elvira' },
    olmo: { model: 'olmoYoung' },
    paz: { model: 'paz' },
    collier1: { model: 'collierA' },
    collier2: { model: 'collierB' },
    collier3: { model: 'collierC' },
  },
  chapters: [
    {
      id: 'spring',
      closedBy: 'ceda',
      piece: 'springLeaf',
      acts: [
        {
          // A leaf of the Grandsire falls in the bastion's Grove, black inside.
          kind: 'scene',
          id: 'cine',
          lines: [],
          cine: {
            video: 'senda-druid-1',
            place: 'groveLeaf',
            sky: 'dusk',
            weather: 'rain',
            cam: [
              { from: [-1.6, 7.4, 3.2], at: [0.2, 0.4, 1.6], ms: 5200 },
              { from: [1.4, 3.2, 1.6], at: [0, 0.2, 0.8], ms: 5200 },
              { from: [-0.8, 1.6, 0.8], at: [0.1, 0, 0.2], ms: 5200 },
            ],
            figures: [],
          },
        },
        {
          kind: 'scene',
          id: 'open',
          lines: [ln('l1', 'woman'), ln('l2', 'woman'), ln('l3', 'ceda')],
          cine: {
            place: 'grove',
            sky: 'dusk',
            weather: 'rain',
            cam: [
              { from: [-3.0, 6.0, 2.4], at: [0.4, -0.6, 0.9], ms: 8000 },
              { from: [-1.2, 3.0, 1.3], at: [0.3, 0, 0.6], ms: 9000 },
              { from: [1.4, 2.2, 1.0], at: [-0.2, 0.2, 0.5], ms: 9000 },
            ],
            figures: [
              { who: 'ceda', q: -1, r: 0, facing: 0.3, pose: 'stand' },
              { who: 'hero', q: 1, r: 0, facing: 3.1, pose: 'stand' },
            ],
            structures: [{ type: 'grove', q: 0, r: -3 }],
            alt: {
              unless: 'grove',
              cine: {
                place: 'groveYard',
                sky: 'dusk',
                weather: 'rain',
                cam: [
                  { from: [-3.0, 6.0, 2.4], at: [0.4, -0.6, 0.9], ms: 8000 },
                  { from: [-1.2, 3.0, 1.3], at: [0.3, 0, 0.6], ms: 9000 },
                  { from: [1.4, 2.2, 1.0], at: [-0.2, 0.2, 0.5], ms: 9000 },
                ],
                figures: [
                  { who: 'ceda', q: -1, r: 0, facing: 0.3, pose: 'stand' },
                  { who: 'hero', q: 1, r: 0, facing: 3.1, pose: 'stand' },
                ],
              },
            },
          },
        },
        {
          // The Circle's low wood at the Grandsire's foot: the thaw coming down between the trees (§3, act 1).
          kind: 'fight',
          id: 'thaw',
          mode: 'thaw',
          place: 'floodWood',
          weather: 'rain',
          budgetBp: 9000,
          goal: { kind: 'glade' },
          says: [
            { on: 'start', who: 'ceda', line: 'l1', delay: sec(2) },
            { on: 'cue', cue: 'surge1', who: 'ceda', line: 'l2' },
            { on: 'cue', cue: 'wet', who: 'ceda', line: 'l3' },
            { on: 'cue', cue: 'wolves', who: 'ceda', line: 'l4', delay: sec(1) },
            { on: 'cue', cue: 'rise', who: 'ceda', line: 'l5' },
          ],
        },
        {
          // In the clearing a sooty man up a tree, the water below, a sack on his shoulder.
          kind: 'scene',
          id: 'tree',
          lines: [ln('l1', 'collier'), ln('l2', 'ceda'), ln('l3', 'hob')],
          cine: {
            place: 'olmoGlade',
            sky: 'day',
            weather: 'rain',
            cam: [
              { from: [-3.2, 5.8, 2.6], at: [0.4, -1.2, 1.4], ms: 8000 },
              { from: [-1.0, 2.4, 1.3], at: [0.4, -1.4, 1.6], ms: 9000 },
              { from: [1.4, 2.2, 1.0], at: [-0.2, -0.4, 0.6], ms: 9000 },
            ],
            figures: [
              { who: 'hob', q: 1, r: -2, facing: 4.6, pose: 'stand' },
              { who: 'ceda', q: -1, r: 1, facing: 0.6, pose: 'stand' },
              { who: 'hero', q: 0, r: 1, facing: 1.4, pose: 'stand' },
            ],
          },
        },
        {
          // Olmo's clearing held while Ceda looks for the face and Hob fences the shoots (§3, act 2).
          kind: 'fight',
          id: 'face',
          mode: 'sprouts',
          place: 'olmoGlade',
          weather: 'rain',
          budgetBp: 9000,
          goal: { kind: 'face' },
          npcs: [
            { id: 'ceda', does: 'wait', hits: 15 },
            { id: 'hob', does: 'work', hits: 15, ticks: sec(5) },
          ],
          says: [
            { on: 'cue', cue: 'eat1', who: 'hob', line: 'l1' },
            { on: 'cue', cue: 'fence2', who: 'hob', line: 'l2', delay: sec(1) },
            { on: 'cue', cue: 'fence2', who: 'ceda', line: 'l3', delay: sec(4) },
            { on: 'half', who: 'ceda', line: 'l4' },
            { on: 'npcHit', npc: 'hob', who: 'hob', line: 'l5' },
          ],
        },
        {
          kind: 'fire',
          id: 'fire',
          with: ['hob'],
          questions: [
            { id: 'circle', lines: [ln('l1', 'hob'), ln('l2', 'hob')], set: ['askedCircle'] },
            { id: 'kin', lines: [ln('l1', 'hob'), ln('l2', 'hob')], set: ['askedHobKin'] },
            { id: 'grandsire', lines: [ln('l1', 'hob'), ln('l2', 'hob')], set: ['askedGrandsire'] },
          ],
          cine: {
            place: 'rootEave',
            sky: 'night',
            weather: 'rain',
            cam: [
              { from: [2.6, 3.0, 2.1], at: [-0.3, -0.3, 0.35], ms: 9000 },
              { from: [-1.6, 3.2, 1.6], at: [0.1, -0.3, 0.4], ms: 9000 },
            ],
            figures: [
              { who: 'hob', q: -1, r: 1, facing: 5.4, pose: 'sit' },
              { who: 'ceda', q: 0, r: 1, facing: 4.6, pose: 'sit' },
              { who: 'hero', q: 1, r: -1, facing: 2.2, pose: 'sit' },
            ],
            fire: { q: 0, r: 0 },
          },
        },
        {
          // The test: the swollen stream, the seed on its islet, the roots that grow toward what sprouts (§4, puzzle 1);
          // the Lodazal coming up out of the flood's mud.
          kind: 'fight',
          id: 'bridge',
          mode: 'rootBridge',
          place: 'stream',
          weather: 'rain',
          budgetBp: 9000,
          goal: { kind: 'bridge' },
          npcs: [{ id: 'ceda', does: 'wait', hits: 15, safe: true, vital: false }],
          says: [
            { on: 'start', who: 'ceda', line: 'l1', delay: sec(2) },
            { on: 'cue', cue: 'tangle1', who: 'ceda', line: 'l2' },
            { on: 'cue', cue: 'bubble1', who: 'ceda', line: 'l3' },
            { on: 'cue', cue: 'braid', who: 'ceda', line: 'l4' },
            { on: 'cue', cue: 'seeds3', who: 'ceda', line: 'l5' },
            { on: 'cue', cue: 'wave1', who: 'hob', line: 'l6' },
            { on: 'cue', cue: 'cut', who: 'ceda', line: 'l7', delay: sec(1) },
            { on: 'cue', cue: 'weave', who: 'ceda', line: 'l8' },
          ],
          trial: true,
        },
        {
          kind: 'scene',
          id: 'close',
          lines: [
            ln('l1', 'narrator'),
            ln('l2', 'ceda'),
            ln('l3', 'hob'),
            ln('l4', 'hob', { flag: 'askedHobKin' }),
            ln('l5', 'narrator'),
            ln('l6', 'oldman'),
            ln('l7', 'ceda'),
            ln('l8', 'espino'),
            ln('l9', 'narrator'),
          ],
          cine: {
            place: 'olmoFace',
            sky: 'dusk',
            weather: 'fog',
            cam: [
              { from: [-2.6, 5.2, 2.4], at: [0.4, -1.4, 1.2], ms: 9000 },
              { from: [-0.6, 2.2, 1.0], at: [0.2, -1.8, 1.2], ms: 9000 },
              { from: [2.0, 2.4, 1.4], at: [-1.0, -2.0, 0.8], ms: 9000 },
            ],
            figures: [
              { who: 'ceda', q: 0, r: -1, facing: 1.6, pose: 'kneel' },
              { who: 'hob', q: -1, r: 0, facing: 1.2, pose: 'stand' },
              { who: 'hero', q: 1, r: 0, facing: 2.0, pose: 'stand' },
              { who: 'espino', q: -3, r: -2, facing: 0.4, pose: 'stand' },
            ],
          },
        },
      ],
    },
    {
      // Chapter 2, summer («Verano»): months later, smoke over the Slope of the kilns; Hob's kilns covered while the ash
      // wyverns set them alight; Espino on a stone by the pine, watching the fire; the strip opened before the great fire;
      // the campfire on a flat stone over the burning valley (to Ceda); the test, the counterfire; the close with the
      // second face of Olmo, and the face of Hob's kilns.
      id: 'summer',
      closedBy: 'hob',
      piece: 'summerLeaf',
      acts: [
        {
          // Smoke over the Slope of the kilns: the mounds smoking in a row, Los Hornos below, the old pine up top with its
          // great closed cone; a fire coming up from the valley.
          kind: 'scene',
          id: 'cine',
          lines: [],
          cine: {
            video: 'senda-druid-2',
            place: 'kilnCine',
            sky: 'day',
            weather: 'clear',
            cam: [
              { from: [0, 15, 7], at: [0, 1, 0], ms: 5200 },
              { from: [-6, 7, 3], at: [1, -2, 0.5], ms: 5200 },
              { from: [4, -5, 2.4], at: [7, -12, 3.6], ms: 5200 },
            ],
            figures: [],
          },
        },
        {
          kind: 'scene',
          id: 'open',
          lines: [ln('l1', 'ceda'), ln('l2', 'hob'), ln('l3', 'ceda'), ln('l4', 'hob', { flag: 'askedCircle' })],
          cine: {
            place: 'kilnSlope',
            sky: 'day',
            weather: 'clear',
            cam: [
              { from: [-4, 11, 3.2], at: [-2, 5.5, 0.4], ms: 8000 },
              { from: [-5.5, 9, 1.6], at: [-2, 5.5, 0.6], ms: 9000 },
              { from: [0.5, 8.5, 1.4], at: [-2.5, 5.6, 0.5], ms: 9000 },
            ],
            figures: [
              { who: 'ceda', q: -3, r: 6, facing: 0.3, pose: 'stand' },
              { who: 'hob', q: -2, r: 5, facing: 1.6, pose: 'stand' },
              { who: 'hero', q: -1, r: 6, facing: 3.1, pose: 'stand' },
            ],
          },
        },
        {
          // Hob's slope: tall yellow grass, hot stone, the kilns smoking; ash wyverns set them alight from the air (§3, act 1).
          kind: 'fight',
          id: 'kilns',
          mode: 'kilns',
          place: 'kilnSlope',
          weather: 'clear',
          budgetBp: 10000,
          goal: { kind: 'kilns' },
          npcs: [{ id: 'hob', does: 'work', hits: 15, ticks: sec(5), drop: 3 }],
          says: [
            { on: 'start', who: 'hob', line: 'l1', delay: sec(2) },
            { on: 'cue', cue: 'dive1', who: 'ceda', line: 'l2' },
            { on: 'cue', cue: 'lit1', who: 'hob', line: 'l3' },
            { on: 'cue', cue: 'crack1', who: 'hob', line: 'l4' },
            { on: 'cue', cue: 'cover3', who: 'hob', line: 'l5', delay: sec(1) },
            { on: 'cue', cue: 'burst1', who: 'ceda', line: 'l6', delay: sec(1) },
            { on: 'npcHit', npc: 'hob', who: 'hob', line: 'l7' },
          ],
        },
        {
          // By the pine, the fire below: Espino on a stone, watching it.
          kind: 'scene',
          id: 'pine',
          lines: [ln('l1', 'espino'), ln('l2', 'hob'), ln('l3', 'espino'), ln('l4', 'narrator'), ln('l5', 'hob', { flag: 'askedHobKin' })],
          cine: {
            place: 'oldPine',
            sky: 'dusk',
            weather: 'clear',
            cam: [
              { from: [-3, 1, 3.6], at: [1, -5, 1], ms: 9000 },
              { from: [-4.5, -5.5, 1.6], at: [0, -4.5, 0.6], ms: 9000 },
              { from: [2.5, -6.2, 1.8], at: [-0.5, -4.2, 0.6], ms: 9000 },
            ],
            figures: [
              { who: 'espino', q: 1, r: -5, facing: 1.5, pose: 'sit' },
              { who: 'ceda', q: -1, r: -4, facing: 5.4, pose: 'stand' },
              { who: 'hob', q: 0, r: -4, facing: 5.2, pose: 'stand' },
              { who: 'hero', q: -2, r: -4, facing: 5.6, pose: 'stand' },
            ],
          },
        },
        {
          // The old pine with the second face; the great fire coming up from the valley; Hob fells a strip (§3, act 2).
          kind: 'fight',
          id: 'strip',
          mode: 'firebreak',
          place: 'oldPine',
          weather: 'clear',
          budgetBp: 10000,
          goal: { kind: 'strip' },
          npcs: [{ id: 'hob', does: 'work', hits: 15, ticks: sec(5) }],
          says: [
            { on: 'start', who: 'hob', line: 'l1', delay: sec(2) },
            { on: 'cue', cue: 'ember1', who: 'ceda', line: 'l2' },
            { on: 'half', who: 'hob', line: 'l3' },
            { on: 'cue', cue: 'near', who: 'ceda', line: 'l4' },
            { on: 'npcHit', npc: 'hob', who: 'hob', line: 'l5' },
            { on: 'cue', cue: 'done4', who: 'hob', line: 'l6' },
          ],
        },
        {
          kind: 'fire',
          id: 'fire',
          with: ['ceda'],
          questions: [
            { id: 'olmo', lines: [ln('l1', 'ceda'), ln('l2', 'ceda')], set: ['askedOlmo'] },
            { id: 'hebe', lines: [ln('l1', 'ceda'), ln('l2', 'ceda')], set: ['askedHebe'] },
            { id: 'others', lines: [ln('l1', 'ceda'), ln('l2', 'ceda')], set: ['askedOthers'] },
          ],
          cine: {
            place: 'nightRock',
            sky: 'night',
            weather: 'clear',
            cam: [
              { from: [2.6, 3.0, 2.1], at: [-0.3, -0.3, 0.35], ms: 9000 },
              { from: [-1.6, 3.2, 1.6], at: [0.1, -0.3, 0.4], ms: 9000 },
            ],
            figures: [
              { who: 'ceda', q: -1, r: 1, facing: 5.4, pose: 'sit' },
              { who: 'hob', q: 1, r: 1, facing: 4.0, pose: 'lie' },
              { who: 'hero', q: 1, r: -1, facing: 2.2, pose: 'sit' },
            ],
            fire: { q: 0, r: 0 },
          },
        },
        {
          // The test: the slope in patches of grass, the pine between, the great fire below and the smoke that bends
          // (§4, puzzle 2); the old wyvern lighting what you didn't choose.
          kind: 'fight',
          id: 'counter',
          mode: 'counterfire',
          place: 'counterSlope',
          weather: 'clear',
          budgetBp: 10000,
          goal: { kind: 'cone' },
          npcs: [{ id: 'hob', does: 'wait', hits: 15, safe: true, vital: false }],
          says: [
            { on: 'start', who: 'ceda', line: 'l1', delay: sec(2) },
            { on: 'cue', cue: 'bend', who: 'hob', line: 'l2' },
            { on: 'cue', cue: 'wyvLit1', who: 'hob', line: 'l3' },
            { on: 'cue', cue: 'scorch1', who: 'ceda', line: 'l4' },
            { on: 'cue', cue: 'turn', who: 'ceda', line: 'l5', delay: sec(1) },
            { on: 'cue', cue: 'meet', who: 'ceda', line: 'l6' },
            { on: 'cue', cue: 'breath1', who: 'hob', line: 'l7' },
            { on: 'cue', cue: 'dive1', who: 'ceda', line: 'l8' },
          ],
          trial: true,
        },
        {
          kind: 'scene',
          id: 'close',
          lines: [
            ln('l1', 'narrator'),
            ln('l2', 'hob'),
            ln('l3', 'hob', { flag: 'askedHobKin' }),
            ln('l4', 'ceda'),
            ln('l5', 'hob'),
            ln('l6', 'narrator'),
          ],
          cine: {
            place: 'pineFace',
            sky: 'dusk',
            weather: 'clear',
            cam: [
              { from: [-1, -1, 3], at: [2.5, -6.5, 1.6], ms: 9000 },
              { from: [3.6, -5, 1.3], at: [3, -7, 1.4], ms: 9000 },
              { from: [-3, -9, 5], at: [0, 8, 0], ms: 9000 },
            ],
            figures: [
              { who: 'hob', q: 0, r: -5, facing: 5.0, pose: 'stand' },
              { who: 'ceda', q: -1, r: -4, facing: 0.6, pose: 'stand' },
              { who: 'hero', q: 1, r: -4, facing: 1.8, pose: 'stand' },
            ],
          },
        },
      ],
    },
    {
      // Chapter 3, autumn («Otoño»): months later, a red wood over Los Hornos, the earth cracking under the houses; Ruda,
      // Hob's aunt, at her door; the wood crossed with Ceda over what moves the leaves; Ruda's story at the foot of Olmo's
      // maple; the memory, Olmo's autumn a hundred years back, played as Olmo, with Paz and the colliers of then; the
      // campfire at Ruda's by the kilns; the test, what lies below; the close with the cut that isn't the rot's, and
      // Elvira among the trees.
      id: 'autumn',
      closedBy: 'ceda',
      piece: 'autumnLeaf',
      acts: [
        {
          // A red wood over Los Hornos; the leaves fall and under them something moves. Down in the houses, new cracks in
          // the earth, the trees that hold the slope black at their tips.
          kind: 'scene',
          id: 'cine',
          lines: [],
          cine: {
            video: 'senda-druid-3',
            place: 'autumnCine',
            sky: 'dusk',
            weather: 'fog',
            cam: [
              { from: [0, -12, 8], at: [0, -2, 0.5], ms: 5200 },
              { from: [-5, 2, 2.6], at: [2, 6, 0.4], ms: 5200 },
              { from: [3, 8, 1.4], at: [0, 13, 0.6], ms: 5200 },
            ],
            figures: [],
          },
        },
        {
          // In Los Hornos, at the door of a house with kilns.
          kind: 'scene',
          id: 'open',
          lines: [ln('l1', 'ceda'), ln('l2', 'oldwoman'), ln('l3', 'hob'), ln('l4', 'ruda'), ln('l5', 'ruda', { flag: 'askedHobKin' })],
          cine: {
            place: 'hornosTown',
            sky: 'dusk',
            weather: 'fog',
            cam: [
              { from: [-3.4, 5.6, 2.6], at: [0.2, 0.4, 0.9], ms: 8000 },
              { from: [-1.6, 3.0, 1.3], at: [0.4, -0.2, 0.8], ms: 9000 },
              { from: [2.2, 2.0, 1.1], at: [-0.4, -0.4, 0.7], ms: 9000 },
            ],
            figures: [
              { who: 'ruda', q: 0, r: -1, facing: 1.6, pose: 'stand' },
              { who: 'hob', q: 1, r: 0, facing: 3.6, pose: 'stand' },
              { who: 'ceda', q: -1, r: 1, facing: 5.6, pose: 'stand' },
              { who: 'hero', q: 0, r: 1, facing: 4.7, pose: 'stand' },
            ],
          },
        },
        {
          // The autumn wood over the town: leaves to the ankles, an afternoon mist; stalkers under the leaves, weavers
          // that cocoon; Ceda ahead, toward Olmo's maple (§3, act 1).
          kind: 'fight',
          id: 'leaves',
          mode: 'leaves',
          place: 'autumnWood',
          weather: 'fog',
          budgetBp: 11000,
          goal: { kind: 'maple' },
          npcs: [
            { id: 'ceda', does: 'wait', hits: DRUID_PATH.leaves.hits },
            { id: 'hob', does: 'follow', hits: 15, safe: true, vital: false, follow: 2 },
          ],
          says: [
            { on: 'start', who: 'ceda', line: 'l1', delay: sec(2) },
            { on: 'cue', cue: 'furrow1', who: 'ceda', line: 'l2' },
            { on: 'cue', cue: 'surf1', who: 'hob', line: 'l3' },
            { on: 'cue', cue: 'surf3', who: 'hob', line: 'l4' },
            { on: 'cue', cue: 'caught1', who: 'ceda', line: 'l5' },
            { on: 'cue', cue: 'spin1', who: 'ceda', line: 'l6' },
            { on: 'cue', cue: 'cocoon1', who: 'ceda', line: 'l7' },
            { on: 'half', who: 'hob', line: 'l8' },
            { on: 'npcHit', npc: 'ceda', who: 'ceda', line: 'l9' },
          ],
        },
        {
          // At the maple's foot, Ruda come up with them.
          kind: 'scene',
          id: 'maple',
          lines: [ln('l1', 'ruda'), ln('l2', 'ruda'), ln('l3', 'ruda'), ln('l4', 'hob'), ln('l5', 'ceda', { flag: 'askedOlmo' })],
          cine: {
            place: 'mapleFoot',
            sky: 'dusk',
            weather: 'fog',
            cam: [
              { from: [1.0, -1.0, 3.0], at: [3.6, -6.4, 0.8], ms: 9000 },
              { from: [1.6, -3.2, 1.3], at: [3.4, -6.0, 0.6], ms: 9000 },
              { from: [6.0, -4.2, 1.2], at: [3, -6.2, 0.6], ms: 9000 },
            ],
            figures: [
              { who: 'ruda', q: 3, r: -6, facing: 1.2, pose: 'stand' },
              { who: 'hob', q: 4, r: -6, facing: 3.3, pose: 'stand' },
              { who: 'ceda', q: 2, r: -5, facing: 0.4, pose: 'stand' },
              { who: 'hero', q: 3, r: -5, facing: 2.4, pose: 'stand' },
            ],
          },
        },
        {
          // A hundred years back: the scene goes grey. The same wood, younger; the maple young; a few houses below.
          kind: 'scene',
          id: 'past',
          lines: [ln('l1', 'narrator'), ln('l2', 'paz'), ln('l3', 'olmo'), ln('l4', 'paz')],
          cine: {
            place: 'autumnPast',
            sky: 'day',
            memory: true,
            cam: [
              { from: [-3, 8, 9], at: [3, -4, 0.5], ms: 8000 },
              { from: [1.2, -3.2, 1.4], at: [3.4, -6.4, 0.9], ms: 9000 },
              { from: [5.8, -5.2, 1.1], at: [3, -6.4, 0.8], ms: 9000 },
            ],
            figures: [
              { who: 'olmo', q: 3, r: -6, facing: 4.6, pose: 'stand' },
              { who: 'paz', q: 4, r: -6, facing: 2.2, pose: 'stand' },
              { who: 'collier1', q: 5, r: -6, facing: 2.8, pose: 'stand' },
              { who: 'collier2', q: 2, r: -5, facing: 0.4, pose: 'stand' },
            ],
          },
        },
        {
          // The memory (docs/SENDAS.md, emotion, piece 9): played as Olmo, young, with Paz and the colliers of then; the
          // rot comes down the roots toward Los Hornos (§3, act 2; battle/druid-autumn.ts `blight`).
          kind: 'fight',
          id: 'blight',
          mode: 'blight',
          place: 'autumnPast',
          budgetBp: 11000,
          memory: 'olmo',
          goal: { kind: 'houses' },
          npcs: [
            { id: 'paz', does: 'follow', hits: 15, follow: 2 },
            { id: 'collier1', does: 'wait', hits: 10, vital: false },
            { id: 'collier2', does: 'wait', hits: 10, vital: false },
            { id: 'collier3', does: 'wait', hits: 10, vital: false },
          ],
          says: [
            { on: 'start', who: 'olmo', line: 'l1', delay: sec(2) },
            { on: 'cue', cue: 'fork1', who: 'olmo', line: 'l2' },
            { on: 'at', t: sec(20), who: 'paz', line: 'l3' },
            { on: 'cue', cue: 'olmoHalf', who: 'paz', line: 'l4' },
            { on: 'cue', cue: 'near', who: 'paz', line: 'l5' },
            { on: 'cue', cue: 'cut1', who: 'collier1', line: 'l6', delay: sec(1) },
            { on: 'npcHit', npc: 'paz', who: 'paz', line: 'l7' },
            { on: 'cue', cue: 'seed', who: 'paz', line: 'l8' },
          ],
        },
        {
          // Won: the memory's last of it. Olmo gathers the seed; from the maple, the slope going grey, top to bottom.
          kind: 'scene',
          id: 'carve',
          lines: [ln('l1', 'narrator'), ln('l2', 'paz'), ln('l3', 'olmo'), ln('l4', 'paz'), ln('l5', 'olmo'), ln('l6', 'narrator')],
          cine: {
            place: 'mapleCarve',
            sky: 'dusk',
            memory: true,
            cam: [
              { from: [1.6, -4.4, 1.6], at: [4, -7.8, 1.0], ms: 9000 },
              { from: [3.4, -5.4, 1.0], at: [4, -7.9, 0.8], ms: 9000 },
              { from: [4.2, -9, 3.6], at: [-3, 8, 0], ms: 9000 },
            ],
            figures: [
              { who: 'olmo', q: 4, r: -7, facing: 4.7, pose: 'kneel' },
              { who: 'paz', q: 5, r: -7, facing: 3.4, pose: 'stand' },
            ],
          },
        },
        {
          kind: 'fire',
          id: 'fire',
          with: ['ceda', 'hob'],
          questions: [
            { id: 'heal', lines: [ln('l1', 'ceda'), ln('l2', 'ceda')], set: ['askedHeal'] },
            { id: 'fall', lines: [ln('l1', 'ceda'), ln('l2', 'ceda')], set: ['askedFall'] },
            { id: 'hobWhy', lines: [ln('l1', 'hob'), ln('l2', 'hob')], set: ['askedHobWhy'] },
          ],
          cine: {
            place: 'rudaYard',
            sky: 'night',
            cam: [
              { from: [2.6, 3.0, 2.1], at: [-0.3, -0.3, 0.35], ms: 9000 },
              { from: [-1.6, 3.2, 1.6], at: [0.1, -0.3, 0.4], ms: 9000 },
            ],
            figures: [
              { who: 'ceda', q: -1, r: 1, facing: 5.4, pose: 'sit' },
              { who: 'hob', q: 1, r: 0, facing: 3.9, pose: 'sit' },
              { who: 'ruda', q: 0, r: -1, facing: 1.6, pose: 'sit' },
              { who: 'hero', q: 1, r: 1, facing: 4.4, pose: 'sit' },
            ],
            fire: { q: 0, r: 0 },
          },
        },
        {
          // The test: the net of roots by Olmo's maple, part in sight and part under the ground, the mushrooms that say
          // what lies below (§4, puzzle 3); the broodmother webbing the maple.
          kind: 'fight',
          id: 'under',
          mode: 'underroots',
          place: 'mapleRoots',
          weather: 'fog',
          budgetBp: 11000,
          goal: { kind: 'rot' },
          npcs: [
            { id: 'ceda', does: 'wait', hits: 15, safe: true, vital: false },
            { id: 'hob', does: 'wait', hits: 15, safe: true, vital: false },
          ],
          says: [
            { on: 'start', who: 'ceda', line: 'l1', delay: sec(2) },
            { on: 'cue', cue: 'wrong1', who: 'hob', line: 'l2' },
            { on: 'cue', cue: 'wake', who: 'ceda', line: 'l3', delay: sec(1) },
            { on: 'cue', cue: 'vain1', who: 'ceda', line: 'l4' },
            { on: 'cue', cue: 'in1', who: 'ceda', line: 'l5' },
            { on: 'cue', cue: 'web1', who: 'hob', line: 'l6' },
            { on: 'cue', cue: 'block2', who: 'ceda', line: 'l7', delay: sec(1) },
            { on: 'cue', cue: 'leap1', who: 'hob', line: 'l8' },
            { on: 'cue', cue: 'out', who: 'ceda', line: 'l9' },
          ],
          trial: true,
        },
        {
          kind: 'scene',
          id: 'close',
          lines: [
            ln('l1', 'narrator'),
            ln('l2', 'ceda'),
            ln('l3', 'hob'),
            ln('l4', 'ceda'),
            ln('l5', 'ceda', { flag: 'askedFall' }),
            ln('l6', 'narrator'),
            ln('l7', 'ceda'),
            ln('l8', 'elvira'),
            ln('l9', 'ceda'),
            ln('l10', 'narrator'),
          ],
          cine: {
            place: 'mapleFace',
            sky: 'dusk',
            weather: 'fog',
            cam: [
              { from: [-0.6, 0.6, 3.0], at: [2.4, -5, 0.7], ms: 9000 },
              { from: [0.6, -1.8, 1.2], at: [2.2, -4.2, 0.4], ms: 9000 },
              { from: [4.6, -3.2, 1.6], at: [-1.6, -2.6, 0.9], ms: 9000 },
              { from: [3.2, -6.6, 2.4], at: [-4, 9, 0], ms: 9000 },
            ],
            figures: [
              { who: 'ceda', q: 2, r: -4, facing: 1.6, pose: 'kneel' },
              { who: 'hob', q: 3, r: -5, facing: 1.4, pose: 'stand' },
              { who: 'hero', q: 1, r: -4, facing: 0.9, pose: 'stand' },
              { who: 'elvira', q: -1, r: -3, facing: 0.2, pose: 'stand' },
            ],
          },
        },
      ],
    },
    {
      // Chapter 4, winter («Invierno»): months later, the Grandsire's crown over the clouds, its sap frozen; up the
      // boughs under the snow; Hob's leg bandaged in a hollow while the serpents come out of the wood; the campfire of
      // the truth with Hob asleep; the test, the sap of the crown; the lowest point: Olmo's fourth face, Espino's knife,
      // Hob falling through the sea of clouds; the path's one decision, and its consequence in the same scene.
      id: 'winter',
      closedBy: 'ceda',
      piece: 'winterLeaf',
      acts: [
        {
          // The Grandsire's crown over the clouds: boughs as wide as a road, heavy with snow, a white sea below hiding the
          // wood; the sap still in the boughs.
          kind: 'scene',
          id: 'cine',
          lines: [],
          cine: {
            video: 'senda-druid-4',
            place: 'crownCine',
            sky: 'day',
            weather: 'snow',
            cam: [
              { from: [0, 16, -2], at: [0, 2, 3], ms: 5200 },
              { from: [-7, 6, 4], at: [1, -2, 1], ms: 5200 },
              { from: [3, -4, 3], at: [0, -9, 1.4], ms: 5200 },
            ],
            figures: [],
          },
        },
        {
          kind: 'scene',
          id: 'open',
          lines: [ln('l1', 'ceda'), ln('l2', 'ceda'), ln('l3', 'hob'), ln('l4', 'hob', { flag: 'askedGrandsire' })],
          cine: {
            place: 'crownBoughs',
            sky: 'day',
            weather: 'snow',
            cam: [
              { from: [-3.2, 12.6, 2.8], at: [-0.6, 8.2, 0.9], ms: 8000 },
              { from: [-2.6, 10.2, 1.4], at: [-0.8, 8.4, 0.8], ms: 9000 },
              { from: [0.8, 10.4, 1.3], at: [-1.4, 8.6, 0.7], ms: 9000 },
            ],
            figures: [
              { who: 'ceda', q: -6, r: 9, facing: 4.9, pose: 'stand' },
              { who: 'hob', q: -5, r: 9, facing: 4.4, pose: 'stand' },
              { who: 'hero', q: -6, r: 10, facing: 5.2, pose: 'stand' },
            ],
          },
        },
        {
          // Up the boughs over the clouds, with the wind; snow off the boughs above in blocks; soot crows (§3, act 1).
          kind: 'fight',
          id: 'boughs',
          mode: 'boughs',
          place: 'crownBoughs',
          weather: 'snow',
          budgetBp: 12000,
          goal: { kind: 'crown' },
          npcs: [
            { id: 'ceda', does: 'follow', hits: 15, safe: true, vital: false, follow: 3 },
            { id: 'hob', does: 'follow', hits: 15, safe: true, vital: false, follow: 4 },
          ],
          says: [
            { on: 'start', who: 'ceda', line: 'l1', delay: sec(2) },
            { on: 'cue', cue: 'creak1', who: 'ceda', line: 'l2' },
            { on: 'cue', cue: 'struck1', who: 'hob', line: 'l3' },
            { on: 'cue', cue: 'flock1', who: 'hob', line: 'l4' },
            { on: 'half', who: 'hob', line: 'l5' },
            { on: 'cue', cue: 'heap2', who: 'ceda', line: 'l6' },
            { on: 'cue', cue: 'near', who: 'ceda', line: 'l7' },
            { on: 'cue', cue: 'bridge1', who: 'ceda', line: 'l8' },
          ],
        },
        {
          // A hollow of the crown: Hob slipped and hurt his leg; Ceda bandages it while the serpents come out of the wood
          // next to whoever stands still (§3, act 2).
          kind: 'fight',
          id: 'hollow',
          mode: 'hollow',
          place: 'crownHollow',
          weather: 'snow',
          budgetBp: 12000,
          goal: { kind: 'bandage' },
          npcs: [
            { id: 'hob', does: 'wait', hits: DRUID_PATH.hollow.hob },
            { id: 'ceda', does: 'wait', hits: DRUID_PATH.hollow.ceda },
          ],
          says: [
            { on: 'start', who: 'hob', line: 'l1', delay: sec(1) },
            { on: 'start', who: 'ceda', line: 'l2', delay: sec(6.5) },
            { on: 'cue', cue: 'swell3', who: 'hob', line: 'l3' },
            { on: 'cue', cue: 'tread1', who: 'ceda', line: 'l4' },
            { on: 'cue', cue: 'still1', who: 'ceda', line: 'l5' },
            { on: 'npcHit', npc: 'hob', who: 'hob', line: 'l6' },
            { on: 'half', who: 'ceda', line: 'l7' },
          ],
        },
        {
          // In the hollow, Ceda ties off the bandage.
          kind: 'scene',
          id: 'bandage',
          lines: [ln('l1', 'hob'), ln('l2', 'ceda'), ln('l3', 'hob'), ln('l4', 'narrator'), ln('l5', 'ceda'), ln('l6', 'hob')],
          cine: {
            place: 'crownHollow',
            sky: 'dusk',
            weather: 'snow',
            cam: [
              { from: [1.8, 2.6, 1.6], at: [-0.6, -0.2, 0.4], ms: 9000 },
              { from: [-2.4, 1.6, 1.0], at: [-0.4, 0, 0.3], ms: 9000 },
              { from: [0.6, -1.8, 0.9], at: [-0.6, 0.2, 0.3], ms: 9000 },
            ],
            figures: [
              { who: 'hob', q: -1, r: 0, facing: 0.2, pose: 'sit' },
              { who: 'ceda', q: 0, r: 0, facing: 3.0, pose: 'kneel' },
              { who: 'hero', q: 1, r: 1, facing: 3.8, pose: 'stand' },
            ],
          },
        },
        {
          // The campfire of the truth: in the hollow at night, an aurora over it and the sea of clouds below; Hob asleep.
          kind: 'fire',
          id: 'fire',
          with: ['ceda'],
          questions: [
            { id: 'cedaThinks', lines: [ln('l1', 'ceda'), ln('l2', 'ceda')], set: ['askedCedaThinks'] },
            { id: 'noTell', lines: [ln('l1', 'ceda'), ln('l2', 'ceda')], set: ['askedNoTell'] },
            { id: 'hob', lines: [ln('l1', 'ceda'), ln('l2', 'ceda')], set: ['askedHob'] },
          ],
          cine: {
            place: 'crownNight',
            sky: 'night',
            cam: [
              { from: [2.6, 3.4, 1.6], at: [-1.0, -2.2, 0.75], ms: 9000 },
              { from: [-1.8, 3.2, 1.2], at: [0.9, -2.6, 1.3], ms: 9000 },
            ],
            figures: [
              { who: 'ceda', q: -1, r: 1, facing: 5.4, pose: 'sit' },
              { who: 'hob', q: 1, r: 0, facing: 3.4, pose: 'lie' },
              { who: 'hero', q: 1, r: -1, facing: 2.2, pose: 'sit' },
            ],
            fire: { q: 0, r: 0 },
          },
        },
        {
          // The test: the crown's forks and its buds, each with its notches; the knots that shut a branch; the frost
          // that climbs the trunk and opens them (§4, puzzle 4). Hob in his hollow, still where they come out.
          kind: 'fight',
          id: 'sap',
          mode: 'sap',
          place: 'crownSap',
          weather: 'snow',
          budgetBp: 12000,
          goal: { kind: 'sap' },
          npcs: [
            { id: 'hob', does: 'wait', hits: DRUID_PATH.sap.hob },
            { id: 'ceda', does: 'wait', hits: 15, safe: true, vital: false },
          ],
          says: [
            { on: 'start', who: 'ceda', line: 'l1', delay: sec(2) },
            { on: 'cue', cue: 'swell1', who: 'hob', line: 'l2' },
            { on: 'cue', cue: 'crack1', who: 'ceda', line: 'l3' },
            { on: 'cue', cue: 'chill1', who: 'ceda', line: 'l4', delay: sec(1) },
            { on: 'cue', cue: 'run0', who: 'ceda', line: 'l5' },
            { on: 'cue', cue: 'frost1', who: 'hob', line: 'l6' },
            { on: 'cue', cue: 'opened1', who: 'ceda', line: 'l7' },
            { on: 'cue', cue: 'claw1', who: 'hob', line: 'l8' },
            { on: 'npcHit', npc: 'hob', who: 'hob', line: 'l9' },
            { on: 'cue', cue: 'hold0', who: 'ceda', line: 'l10' },
          ],
          trial: true,
        },
        {
          // The lowest point: the sap runs; Olmo's fourth face on the highest bough; Espino's knife; Hob falls.
          kind: 'scene',
          id: 'fall',
          lines: [
            ln('l1', 'narrator'),
            ln('l2', 'hob'),
            ln('l3', 'hob'),
            ln('l4', 'narrator'),
            ln('l5', 'espino'),
            { id: 'l6', who: 'narrator', cue: 'cut' },
            ln('l7', 'hob'),
            { id: 'l8', who: 'narrator', cue: 'fall' },
            ln('l9', 'narrator', { flag: 'askedHobWhy' }),
            ln('l10', 'narrator'),
          ],
          cine: {
            place: 'crownTop',
            sky: 'dusk',
            weather: 'snow',
            cam: [
              // Hob under the highest bough, reading Olmo's fourth face.
              { from: [1.2, 3.4, 1.5], at: [-1.8, -1.8, 1.5], ms: 9000 },
              // Espino in the fork, his knife to the bough Ceda walks out on.
              { from: [-3.8, 2.6, 1.2], at: [1.4, 0.4, 0.6], ms: 9000 },
              // The bough out over the cloud.
              { from: [-1.2, -1.6, 2.2], at: [4.2, 1.8, -0.4], ms: 9000 },
              // Down after it into the cloud.
              { from: [2.4, 0.2, 2.8], at: [5.2, 2.4, -3.2], ms: 9000 },
            ],
            figures: [
              { who: 'hob', q: 3, r: 1, facing: 3.2, pose: 'stand' },
              { who: 'ceda', q: 1, r: 1, facing: 0.2, pose: 'stand' },
              { who: 'espino', q: -2, r: -1, facing: 0.4, pose: 'stand' },
              { who: 'hero', q: -1, r: 2, facing: 0.6, pose: 'stand' },
            ],
          },
        },
        {
          kind: 'choice',
          id: 'choice',
          lines: [ln('l1', 'ceda'), ln('l2', 'ceda', { flag: 'askedFall' }), ln('l5', 'narrator'), ln('l3', 'ceda'), ln('l4', 'ceda')],
          options: [
            { id: 'heal', set: ['healedGrandsire'] },
            { id: 'release', set: ['releasedGrandsire'] },
          ],
          cine: {
            place: 'crownTop',
            sky: 'dusk',
            weather: 'snow',
            cam: [
              { from: [-2.0, 3.2, 1.4], at: [-0.6, 0.2, 0.6], ms: 9000 },
              { from: [1.4, 2.4, 1.2], at: [-0.6, 0.2, 0.6], ms: 9000 },
            ],
            figures: [
              { who: 'ceda', q: -1, r: 0, facing: 1.0, pose: 'kneel' },
              { who: 'hero', q: 0, r: 1, facing: 3.6, pose: 'stand' },
            ],
          },
        },
        {
          kind: 'scene',
          id: 'close',
          lines: [
            { id: 'l1', who: 'narrator', if: { flag: 'healedGrandsire' }, cue: 'heal' },
            ln('l2', 'ceda', { flag: 'healedGrandsire' }),
            { id: 'l3', who: 'narrator', if: { flag: 'releasedGrandsire' }, cue: 'release' },
            ln('l4', 'ceda', { flag: 'releasedGrandsire' }),
            ln('l5', 'ceda', { flag: 'askedHob' }),
            ln('l6', 'narrator'),
          ],
          cine: {
            place: 'crownTop',
            sky: 'dusk',
            weather: 'snow',
            cam: [
              { from: [-1.4, 3.0, 1.8], at: [-0.6, 0, 0.9], ms: 9000 },
              { from: [3.0, 5.4, 3.4], at: [1.6, 3.0, -2.8], ms: 9000 },
              { from: [0.4, 2.8, 1.2], at: [-0.8, 0.2, 0.8], ms: 9000 },
            ],
            figures: [
              { who: 'ceda', q: -1, r: 0, facing: 1.0, pose: 'stand' },
              { who: 'hero', q: 0, r: 1, facing: 3.6, pose: 'stand' },
            ],
          },
        },
      ],
    },
    {
      // Chapter 5, inside the Grandsire («Las raíces»): a year on, the Grandsire close at its foot for the first time;
      // down inside its trunk (the bark, a crypt's halls re-dressed as living wood, Espino's cuts in the walls); the buds
      // below (the sap's rule and the roots'); the campfire in a hollow of the trunk; the net round the chamber (what lies
      // below); Espino at the chamber's mouth with a new knife, the colliers with Hob's ember and Elvira; the speech; the
      // Carcoma; the close with Olmo's last face; the epilogue up in the light, by the decision.
      id: 'roots',
      closedBy: 'ceda',
      piece: 'seasonLook',
      call: true,
      // Lost to the Carcoma, a closed chapter opens again at its fight: the bark, the buds and the net stay won.
      keep: 'carcoma',
      acts: [
        {
          // The Grandsire close, at its foot, for the first time: a trunk as wide as a square, breathing; Ceda's hand on
          // the bark; up the wood's path, smoke: the colliers with their ember lanterns.
          kind: 'scene',
          id: 'cine',
          lines: [],
          cine: {
            video: 'senda-druid-5',
            place: 'grandsireFoot',
            sky: 'dusk',
            cam: [
              { from: [0, 9.5, 1.2], at: [0, -2, 4.5], ms: 5600 },
              { from: [-4.2, 3.6, 1.6], at: [0.2, -1.6, 1.4], ms: 5600 },
              { from: [3.4, 5.4, 2.2], at: [-1.6, 9.6, 0.4], ms: 5200 },
            ],
            figures: [
              { who: 'ceda', q: 0, r: 1, facing: 4.7, pose: 'stand' },
              { who: 'ruda', q: -2, r: 8, facing: 4.7, pose: 'walk', speed: 10, walkMs: 9000 },
              { who: 'collier1', q: -1, r: 9, facing: 4.7, pose: 'walk', speed: 10, walkMs: 9000 },
              { who: 'collier2', q: -3, r: 9, facing: 4.7, pose: 'walk', speed: 10, walkMs: 9000 },
            ],
          },
        },
        {
          kind: 'scene',
          id: 'open',
          lines: [ln('l1', 'ceda'), ln('l2', 'ceda'), ln('l3', 'ceda')],
          cine: {
            place: 'grandsireFoot',
            sky: 'dusk',
            cam: [
              { from: [-1.8, 4.6, 1.3], at: [0.2, 0.4, 0.9], ms: 9000 },
              { from: [2.2, 3.0, 1.0], at: [-0.4, 0.6, 1.1], ms: 9000 },
            ],
            figures: [
              { who: 'ceda', q: 0, r: 1, facing: 4.7, pose: 'stand' },
              { who: 'hero', q: 1, r: 1, facing: 4.4, pose: 'stand' },
            ],
          },
        },
        {
          // Act 1, the bark: down inside the trunk, galleries of living wood with the amber light through the bark and
          // the sap dripping; slimes and weavers out of the knots; Espino's smooth cuts in the walls (a crypt's descent).
          kind: 'fight',
          id: 'bark',
          mode: 'descent',
          place: 'bark',
          rooms: 3,
          budgetBp: 9000,
          bestiary: { slime: 3, weaver: 2 },
          goal: { kind: 'reach' },
          npcs: [{ id: 'ceda', does: 'follow', hits: 50, follow: 2 }],
          says: [
            { on: 'at', t: sec(8), who: 'ceda', line: 'l1' },
            { on: 'at', t: sec(13), who: 'ceda', line: 'l2' },
            { on: 'half', who: 'ceda', line: 'l3' },
          ],
        },
        {
          // Act 2, the buds below: the sap down the gallery's forks to the mouths stopped with rot; at each open mouth
          // its sound roots, sown in the order that doesn't tangle (battle/druid-roots.ts `mouths`).
          kind: 'fight',
          id: 'buds',
          mode: 'deepMouths',
          place: 'sapGallery',
          budgetBp: 12000,
          goal: { kind: 'deepMouths' },
          npcs: [{ id: 'ceda', does: 'wait', hits: DRUID_PATH.mouths.ceda }],
          says: [
            { on: 'start', who: 'ceda', line: 'l1', delay: sec(2) },
            { on: 'cue', cue: 'crack1', who: 'ceda', line: 'l2' },
            { on: 'cue', cue: 'open1', who: 'ceda', line: 'l3', delay: sec(1) },
            { on: 'cue', cue: 'tangle1', who: 'ceda', line: 'l4' },
            { on: 'cue', cue: 'clean1', who: 'ceda', line: 'l5' },
            { on: 'cue', cue: 'drip1', who: 'ceda', line: 'l6' },
            { on: 'npcHit', npc: 'ceda', who: 'ceda', line: 'l7' },
          ],
        },
        {
          // The campfire in a hollow of the trunk, the amber light through the bark, before the chamber.
          kind: 'fire',
          id: 'fire',
          with: ['ceda'],
          questions: [
            { id: 'cedaHob', lines: [ln('l1', 'ceda'), ln('l2', 'ceda')], set: ['askedCedaHob'] },
            { id: 'cedaAfter', lines: [ln('l1', 'ceda'), ln('l2', 'ceda', { flag: 'healedGrandsire' }), ln('l3', 'ceda', { flag: 'releasedGrandsire' }), ln('l4', 'ceda')], set: ['askedCedaAfter'] },
            { id: 'carve', lines: [ln('l1', 'ceda'), ln('l2', 'ceda')], set: ['askedCarve'] },
          ],
          cine: {
            place: 'sapHollow',
            sky: 'night',
            cam: [
              { from: [2.4, 2.8, 1.4], at: [-0.4, -0.4, 0.6], ms: 9000 },
              { from: [-2.2, 2.2, 1.1], at: [0.4, -0.2, 0.7], ms: 9000 },
            ],
            figures: [
              { who: 'ceda', q: -1, r: 0, facing: 0.4, pose: 'sit' },
              { who: 'hero', q: 1, r: 0, facing: 3.4, pose: 'sit' },
            ],
            fire: { q: 0, r: 0 },
          },
        },
        {
          // Act 3, what lies below: the net of roots round the Carcoma's chamber, half under the wood; cut the black ones,
          // the hidden too, by the mushrooms, never the pale ones that hold the Grandsire (chapter 3's rule).
          kind: 'fight',
          id: 'net',
          mode: 'deepnet',
          place: 'deepNet',
          budgetBp: 12000,
          goal: { kind: 'net' },
          npcs: [{ id: 'ceda', does: 'wait', hits: 15, safe: true, vital: false }],
          says: [
            { on: 'start', who: 'ceda', line: 'l1', delay: sec(2) },
            { on: 'cue', cue: 'wake', who: 'ceda', line: 'l2' },
            { on: 'cue', cue: 'block1', who: 'ceda', line: 'l3' },
            { on: 'cue', cue: 'wrong1', who: 'ceda', line: 'l4' },
            { on: 'cue', cue: 'leap1', who: 'ceda', line: 'l5' },
            { on: 'half', who: 'ceda', line: 'l6' },
          ],
          trial: true,
        },
        {
          // At the chamber's mouth: Espino with a new curved knife, leaning on the last great sound root; the colliers
          // come down the gallery with their lanterns of embers, Ruda in front, and take the knife off him.
          kind: 'scene',
          id: 'mouth',
          lines: [ln('l1', 'narrator'), ln('l2', 'espino'), ln('l3', 'ceda'), ln('l4', 'espino'), { id: 'l5', who: 'narrator', cue: 'colliers' }, ln('l6', 'ruda')],
          cine: {
            place: 'carcomaMouth',
            sky: 'night',
            cam: [
              { from: [1.6, 5.8, 1.2], at: [-0.6, 2.6, 0.9], ms: 9000 },
              { from: [-2.4, 3.2, 1.0], at: [0.6, 5.4, 0.9], ms: 9000 },
              { from: [0.4, 8.6, 1.6], at: [0, 4.2, 0.8], ms: 9000 },
            ],
            figures: [
              { who: 'espino', q: -1, r: 4, facing: 1.4, pose: 'stand' },
              { who: 'ceda', q: 1, r: 5, facing: 4.0, pose: 'stand' },
              { who: 'hero', q: 0, r: 6, facing: 4.6, pose: 'stand' },
              { who: 'ruda', q: 1, r: 8, facing: 4.7, pose: 'walk', delay: 9000, speed: 9, walkMs: 4000 },
              { who: 'collier1', q: 0, r: 9, facing: 4.7, pose: 'walk', delay: 9000, speed: 9, walkMs: 4000 },
              { who: 'collier2', q: 2, r: 8, facing: 4.7, pose: 'walk', delay: 9000, speed: 9, walkMs: 4000 },
            ],
          },
        },
        {
          // The speech (docs/SENDAS.md, emotion, pieces 7 and 8), at the chamber's mouth with Espino on his knees between
          // two colliers; Elvira behind them, her hood down; the hands on the earth; the hero says the last line.
          kind: 'choice',
          id: 'speech',
          lines: [
            ln('l1', 'narrator'),
            ln('l2', 'ruda'),
            ln('l3', 'ceda'),
            ln('l4', 'ceda'),
            ln('l5', 'ruda'),
            ln('l6', 'ceda'),
            ln('l7', 'elvira'),
            ln('l8', 'ceda'),
            { id: 'l9', who: 'narrator', cue: 'hands' },
            ln('l10', 'ruda'),
            ln('l11', 'ceda'),
            ln('l12', 'ceda'),
          ],
          options: [
            { id: 'forest', set: ['vowForest'] },
            { id: 'hob', set: ['vowHob'] },
          ],
          cine: {
            place: 'carcomaMouth',
            sky: 'night',
            cam: [
              { from: [0.6, 8.8, 1.5], at: [0, 5.2, 0.9], ms: 12000 },
              { from: [-1.8, 4.6, 0.9], at: [0.6, 7.2, 0.8], ms: 14000 },
              { from: [1.4, 5.0, 0.5], at: [-0.4, 6.8, 0.2], ms: 14000 },
            ],
            figures: [
              { who: 'ceda', q: 0, r: 5, facing: 1.6, pose: 'stand' },
              { who: 'hero', q: 1, r: 5, facing: 1.7, pose: 'stand' },
              { who: 'espino', q: -2, r: 7, facing: 4.7, pose: 'kneel' },
              { who: 'collier1', q: -3, r: 7, facing: 0.2, pose: 'stand' },
              { who: 'collier2', q: -1, r: 7, facing: 3.0, pose: 'stand' },
              { who: 'ruda', q: 0, r: 7, facing: 4.7, pose: 'stand' },
              { who: 'elvira', q: 1, r: 8, facing: 4.6, pose: 'stand' },
            ],
          },
        },
        {
          // Act 4: the Carcoma, down at the bottom of all, where the wood touches the titan's flesh (§5;
          // battle/druid-roots.ts `carcoma`). The colliers at the door with axes and lanterns; Elvira by the knots.
          kind: 'fight',
          id: 'carcoma',
          mode: 'carcoma',
          place: 'carcomaChamber',
          night: true,
          budgetBp: 12500,
          goal: { kind: 'carcoma' },
          npcs: [
            { id: 'ruda', does: 'wait', hits: DRUID_PATH.carcoma.colliers, vital: false },
            { id: 'collier1', does: 'wait', hits: DRUID_PATH.carcoma.colliers, vital: false },
            { id: 'collier2', does: 'wait', hits: DRUID_PATH.carcoma.colliers, vital: false },
            { id: 'elvira', does: 'wait', hits: 15, safe: true, vital: false },
            { id: 'ceda', does: 'wait', hits: 15, safe: true, vital: false },
            { id: 'espino', does: 'wait', hits: 1, safe: true, vital: false },
          ],
          says: [
            { on: 'start', who: 'ceda', line: 'l1', delay: sec(2) },
            { on: 'cue', cue: 'cut1', who: 'ceda', line: 'l2' },
            { on: 'cue', cue: 'tangle1', who: 'ceda', line: 'l3' },
            { on: 'cue', cue: 'wrong1', who: 'ceda', line: 'l4' },
            { on: 'cue', cue: 'bare1', who: 'ruda', line: 'l5' },
            { on: 'cue', cue: 'gate1', who: 'elvira', line: 'l6' },
            { on: 'cue', cue: 'thread1', who: 'ceda', line: 'l7' },
            { on: 'cue', cue: 'branch1', who: 'ceda', line: 'l8', if: { flag: 'healedGrandsire' } },
            { on: 'cue', cue: 'seedHit1', who: 'ceda', line: 'l9', if: { flag: 'releasedGrandsire' } },
            { on: 'npcHit', npc: 'ruda', who: 'ruda', line: 'l10' },
            { on: 'cue', cue: 'last', who: 'ceda', line: 'l11' },
          ],
          foe: true,
          trial: true,
        },
        {
          // The Carcoma shrinks to fit in a hand and comes apart in dust; on the wall, Olmo's last face, unfinished.
          kind: 'scene',
          id: 'close',
          lines: [
            ln('l1', 'narrator'),
            ln('l2', 'narrator'),
            ln('l3', 'ceda'),
            { id: 'l4', who: 'narrator', cue: 'carve', if: { flag: 'healedGrandsire' } },
            { id: 'l5', who: 'narrator', cue: 'carve', if: { flag: 'releasedGrandsire' } },
            ln('l6', 'ceda', { flag: 'askedCarve' }),
          ],
          cine: {
            place: 'carcomaChamber',
            sky: 'night',
            face: true,
            cam: [
              { from: [0.6, 2.2, 1.0], at: [0, -0.6, 0.3], ms: 9000 },
              { from: [-1.4, -3.6, 1.4], at: [0, -7.2, 1.1], ms: 10000 },
            ],
            figures: [
              { who: 'ceda', q: -2, r: -4, facing: 4.4, pose: 'stand' },
              // (Beside the face, not before it: Olmo's face on the wall reads past the hero.)
              { who: 'hero', q: 1, r: -6, facing: 4.0, pose: 'stand' },
              { who: 'ruda', q: 1, r: -4, facing: 4.6, pose: 'stand' },
              { who: 'collier1', q: 2, r: -3, facing: 4.4, pose: 'stand' },
            ],
          },
        },
        {
          // Up, by the decision: the Grandsire breathing deeper, its amber turned green, Los Hornos grey below (healed);
          // or the Grandsire lying down over the wood and the light coming in (released). Espino's one thing, going.
          kind: 'scene',
          id: 'up',
          lines: [
            ln('l1', 'narrator', { flag: 'healedGrandsire' }),
            ln('l2', 'ceda', { flag: 'healedGrandsire' }),
            ln('l3', 'ruda', { flag: 'healedGrandsire' }),
            ln('l4', 'ruda', { flag: 'healedGrandsire' }),
            ln('l5', 'narrator', { flag: 'releasedGrandsire' }),
            ln('l6', 'ceda', { flag: 'releasedGrandsire' }),
            ln('l7', 'elvira', { flag: 'releasedGrandsire' }),
            ln('l8', 'ceda', { flag: 'releasedGrandsire' }),
            ln('l9', 'narrator'),
            ln('l10', 'espino', { flag: 'healedGrandsire' }),
            ln('l11', 'espino', { flag: 'releasedGrandsire' }),
          ],
          cine: {
            place: 'grandsireFoot',
            sky: 'dawn',
            breath: true,
            cam: [
              { from: [-2.4, 5.2, 1.6], at: [0, -1.0, 2.6], ms: 9000 },
              { from: [2.0, 6.4, 1.2], at: [-1.2, 9.0, 0.8], ms: 9000 },
            ],
            figures: [
              { who: 'ceda', q: 0, r: 2, facing: 4.7, pose: 'stand' },
              { who: 'hero', q: 1, r: 2, facing: 4.5, pose: 'stand' },
              { who: 'ruda', q: -1, r: 3, facing: 4.8, pose: 'stand' },
              { who: 'espino', q: -2, r: 7, facing: 1.6, pose: 'stand' },
            ],
            swap: {
              if: { flag: 'releasedGrandsire' },
              cine: {
                place: 'grandsireFallen',
                sky: 'dawn',
                cam: [
                  { from: [-3.0, 6.0, 1.8], at: [1.0, -2.0, 1.2], ms: 9000 },
                  { from: [2.0, 6.4, 1.2], at: [-1.2, 9.0, 0.8], ms: 9000 },
                ],
                figures: [
                  { who: 'ceda', q: 0, r: 2, facing: 4.7, pose: 'stand' },
                  { who: 'hero', q: 1, r: 2, facing: 4.5, pose: 'stand' },
                  { who: 'elvira', q: -1, r: 3, facing: 4.8, pose: 'stand' },
                  { who: 'espino', q: -2, r: 7, facing: 1.6, pose: 'stand' },
                ],
              },
            },
          },
        },
        {
          // The epilogue: Ceda planting (the first tree of a kiln down at Musgoviejo, or by the shoot); the line of the
          // speech said back; Ruda and Ceda carving Hob's face on the stone of his oldest kiln, beside Olmo's.
          kind: 'scene',
          id: 'epilogue',
          lines: [
            ln('l1', 'narrator', { flag: 'healedGrandsire' }),
            ln('l2', 'narrator', { flag: 'releasedGrandsire' }),
            ln('l3', 'narrator'),
            ln('l4', 'ceda', { flag: 'vowForest' }),
            ln('l5', 'ceda', { flag: 'vowHob' }),
            ln('l6', 'narrator'),
            ln('l7', 'ruda', { flag: 'askedHobKin' }),
            ln('l8', 'narrator', { flag: 'askedHobWhy' }),
            { id: 'l8', who: 'narrator', if: { flag: 'askedCedaHob', notFlag: 'askedHobWhy' } },
            ln('l9', 'narrator'),
          ],
          cine: {
            video: 'senda-druid-5-end',
            place: 'kilnSlope',
            sky: 'dusk',
            cam: [
              { from: [2.2, 3.4, 1.4], at: [-0.4, -0.2, 0.8], ms: 10000 },
              { from: [-1.6, 2.2, 1.0], at: [0.6, -0.8, 0.7], ms: 10000 },
            ],
            figures: [
              { who: 'ceda', q: 0, r: 0, facing: 4.4, pose: 'kneel' },
              { who: 'ruda', q: 1, r: 0, facing: 3.8, pose: 'kneel' },
              { who: 'hero', q: 0, r: 1, facing: 4.6, pose: 'stand' },
            ],
          },
        },
      ],
    },
  ],
};
